import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, generateCover, uploadCover } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { CoverContext } from "~/types/covers-admin";

/** The i18n key for a failed upload or generation. */
export function translateCoverError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 413) return "covers.errors.tooLarge";
    if (error.status === 422) return "covers.errors.invalid";
    if (error.status === 502) return "covers.errors.failed";
    if (error.status === 503) return "covers.errors.unavailable";
    return "common.somethingWentWrong";
  }
  return "covers.errors.network";
}

export interface CoverPublishing {
  /** Resolves with the published URL, or null once `onError` has been told why. */
  upload: (
    file: File,
    options: { watermark: boolean },
    context: CoverContext,
  ) => Promise<string | null>;
  generate: (
    subject: string | null,
    context: CoverContext,
  ) => Promise<string | null>;
  /** An upload or generation is in flight; the editor's save has to wait for it. */
  busy: boolean;
}

/**
 * New covers for an editor's form — the article, event and highlight editors alike.
 *
 * Both actions only publish into the library and hand the URL back; the form holds it
 * like a library pick and the save commits it. Nothing needs a saved row, so a draft
 * gets the same controls as a published piece.
 *
 * @param onError receives an i18n key, or null when a new attempt clears the last one.
 */
export function useCoverPublishing(
  onError: (key: string | null) => void,
): CoverPublishing {
  const queryClient = useQueryClient();
  const refreshLibrary = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.coversRoot });

  const uploading = useMutation({
    mutationFn: uploadCover,
    onSuccess: refreshLibrary,
  });
  const generating = useMutation({
    mutationFn: generateCover,
    onSuccess: refreshLibrary,
  });

  const upload: CoverPublishing["upload"] = async (
    file,
    { watermark },
    context,
  ) => {
    onError(null);
    try {
      return (await uploading.mutateAsync({ file, watermark, context })).url;
    } catch (error) {
      onError(translateCoverError(error));
      return null;
    }
  };

  const generate: CoverPublishing["generate"] = async (subject, context) => {
    onError(null);
    // The API refuses this too, but its 422 would surface as "that file can't be used".
    if (!context.title.trim() && !subject?.trim()) {
      onError("covers.errors.needsTitle");
      return null;
    }
    try {
      return (await generating.mutateAsync({ subject, context })).url;
    } catch (error) {
      onError(translateCoverError(error));
      return null;
    }
  };

  return {
    upload,
    generate,
    busy: uploading.isPending || generating.isPending,
  };
}
